import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box, Typography, Paper, Grid, Button, TextField, Select, MenuItem,
    FormControl, InputLabel, Alert, CircularProgress, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Stack, InputAdornment,
    Pagination, Dialog, DialogTitle, DialogContent, DialogActions, List,
    ListItem, ListItemText, ListItemIcon, Checkbox, Chip, IconButton,
    Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Search as SearchIcon,
    FilterList as FilterListIcon,
    Factory as FactoryIcon,
    AttachFile as AttachFileIcon,
    Warning as WarningIcon,
    AddCircle as AddCircleIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { API_URL, addProductionItem, updateProductionItem, deleteProductionItem, deleteProductionCompetencia } from '../api/api';
import { useCompetence } from '../contexts/CompetenceContext';

const ProductionPage = () => {
    const { globalCompetence, availableMonths, refreshMonths } = useCompetence();

    const [competencias, setCompetencias] = useState([]);
    const [selectedCompetencia, setSelectedCompetencia] = useState('');
    // uploadCompetencia removed, using selectedCompetencia instead
    // uploadCompetencia removed, using selectedCompetencia instead
    const [file, setFile] = useState(null);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState(null);

    // State for missing cost centers modal
    const [missingCenters, setMissingCenters] = useState([]);
    const [showMissingModal, setShowMissingModal] = useState(false);
    const [selectedCenters, setSelectedCenters] = useState([]);
    const [registering, setRegistering] = useState(false);
    const [ccOptions, setCcOptions] = useState([]);

    // CRUD State
    const [openModal, setOpenModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ centro_custo: '', quantidade: '', descricao: '', valor: '' });
    const [deleteConfirmation, setDeleteConfirmation] = useState(null);

    // Detail Cache State
    const [visitDetails, setVisitDetails] = useState({});
    const [loadingDetails, setLoadingDetails] = useState({});

    // CRUD Handlers
    const handleOpenAdd = () => {
        setEditingItem(null);
        setFormData({ centro_custo: '', quantidade: '', descricao: '', valor: '' });
        setOpenModal(true);
    };

    const handleOpenEdit = (item) => {
        setEditingItem(item);
        setFormData({
            centro_custo: item.centro_custo,
            quantidade: item.quantidade,
            descricao: item.descricao || '',
            valor: item.valor_total || ''
        });
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setEditingItem(null);
    };

    const handleSave = async () => {
        try {
            const payload = {
                ...formData,
                quantidade: parseFloat(formData.quantidade),
                valor: formData.valor ? parseFloat(formData.valor) : null
            };

            if (editingItem) {
                await updateProductionItem(editingItem.id, payload);
                setMessage({ type: 'success', text: 'Item atualizado com sucesso!' });
            } else {
                await addProductionItem({ ...payload, competencia: selectedCompetencia });
                setMessage({ type: 'success', text: 'Item adicionado com sucesso!' });
            }
            handleCloseModal();
            fetchItems();
        } catch (error) {
            console.error('Erro ao salvar item:', error);
            setMessage({ type: 'error', text: 'Erro ao salvar item. Verifique os dados.' });
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirmation) return;
        try {
            await deleteProductionItem(deleteConfirmation);
            setMessage({ type: 'success', text: 'Item excluído com sucesso!' });
            setDeleteConfirmation(null);
            fetchItems();
        } catch (error) {
            console.error('Erro ao excluir item:', error);
            setMessage({ type: 'error', text: 'Erro ao excluir item.' });
        }
    };

    const handleDeleteAllProduction = async () => {
        if (!selectedCompetencia) {
            setMessage({ type: 'error', text: 'Selecione uma competência primeiro.' });
            return;
        }

        if (!window.confirm(`Tem certeza que deseja excluir TODOS os ${total} registros de Produção da competência ${selectedCompetencia}? Esta ação não pode ser desfeita.`)) {
            return;
        }

        try {
            await deleteProductionCompetencia(selectedCompetencia);
            setMessage({ type: 'success', text: 'Todos os registros de Produção foram excluídos com sucesso!' });
            fetchItems();
        } catch (error) {
            console.error('Erro ao excluir produção:', error);
            setMessage({ type: 'error', text: 'Erro ao excluir registros de produção.' });
        }
    };

    useEffect(() => {
        if (availableMonths.length > 0) {
            setCompetencias(availableMonths);
        }
        
        // Fetch CCs for the dropdown
        const fetchCCs = async () => {
            try {
                const res = await axios.get(`${API_URL}/config/centros_custo`);
                if (res.data) {
                    setCcOptions(res.data.map(cc => cc.nome).sort());
                }
            } catch (err) {
                console.error("Erro ao buscar CCs:", err);
            }
        };
        fetchCCs();
    }, [availableMonths]);

    // Sync with global competence
    useEffect(() => {
        if (globalCompetence && competencias.length > 0 && !selectedCompetencia) {
            if (competencias.includes(globalCompetence)) {
                setSelectedCompetencia(globalCompetence);
            }
        }
    }, [globalCompetence, competencias]);

    useEffect(() => {
        if (selectedCompetencia) {
            fetchItems();
        } else {
            setItems([]);
            setTotal(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompetencia, page, search]);

    // fetchCompetencias removed as we use availableMonths from context

    const fetchItems = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/production`, {
                params: {
                    competencia: selectedCompetencia,
                    page,
                    search,
                    per_page: 50
                }
            });
            const data = res.data;
            setItems(data.items || []);
            setTotal(data.total || 0);

            // Limpar cache ao trocar de página/busca
            setVisitDetails({});
            setLoadingDetails({});
        } catch (error) {
            console.error("Erro ao buscar itens:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchVisitDetails = async (atendimento) => {
        if (visitDetails[atendimento]) return; // Já carregado

        setLoadingDetails(prev => ({ ...prev, [atendimento]: true }));
        try {
            const res = await axios.get(`${API_URL}/production/details/${atendimento}`);
            setVisitDetails(prev => ({ ...prev, [atendimento]: res.data }));
        } catch (error) {
            console.error("Erro ao buscar detalhes do atendimento:", error);
            setMessage({ type: 'error', text: 'Erro ao carregar detalhes do atendimento.' });
        } finally {
            setLoadingDetails(prev => ({ ...prev, [atendimento]: false }));
        }
    };

    const handleAccordionToggle = (atendimento) => (event, isExpanded) => {
        if (isExpanded) {
            fetchVisitDetails(atendimento);
        }
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    // Check for missing cost centers
    const checkMissingCostCenters = async (centrosDoArquivo) => {
        try {
            const res = await axios.post(`${API_URL}/production/check_cost_centers`, {
                centros: centrosDoArquivo
            });
            if (res.data.nao_cadastrados && res.data.nao_cadastrados.length > 0) {
                setMissingCenters(res.data.nao_cadastrados);
                setSelectedCenters(res.data.nao_cadastrados);
                setShowMissingModal(true);
            }
        } catch (error) {
            console.error('Erro ao verificar centros de custo:', error);
        }
    };

    // Register selected cost centers
    const handleRegisterCenters = async () => {
        if (selectedCenters.length === 0) {
            setShowMissingModal(false);
            return;
        }

        setRegistering(true);
        try {
            await axios.post(`${API_URL}/production/register_cost_centers`, {
                centros: selectedCenters
            });
            setMessage({ type: 'success', text: `${selectedCenters.length} centro(s) de custo cadastrado(s) com sucesso!` });
            setShowMissingModal(false);
            setMissingCenters([]);
            setSelectedCenters([]);
        } catch (error) {
            setMessage({ type: 'error', text: 'Erro ao cadastrar centros de custo.' });
        } finally {
            setRegistering(false);
        }
    };

    // Toggle center selection
    const toggleCenterSelection = (center) => {
        setSelectedCenters(prev =>
            prev.includes(center)
                ? prev.filter(c => c !== center)
                : [...prev, center]
        );
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        e.preventDefault();
        if (!file || !selectedCompetencia) {
            setMessage({ type: 'error', text: 'Selecione um arquivo e uma competência.' });
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('competencia', selectedCompetencia);

        setUploading(true);
        setMessage(null);

        try {
            const res = await axios.post(`${API_URL}/production/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const data = res.data;

            setMessage({ type: 'success', text: data.message });
            setFile(null);
            const fileInput = document.getElementById('fileInput');
            if (fileInput) fileInput.value = '';
            refreshMonths();
            fetchItems();

            // Check for missing cost centers after successful upload
            if (items.length > 0 || data.message) {
                try {
                    const prodRes = await axios.get(`${API_URL}/production`, {
                        params: { competencia: selectedCompetencia, per_page: 1000 }
                    });
                    const centros = [...new Set(prodRes.data.items?.map(i => i.centro_custo).filter(Boolean) || [])];
                    if (centros.length > 0) {
                        await checkMissingCostCenters(centros);
                    }
                } catch (err) {
                    console.error('Erro ao buscar centros após upload:', err);
                }
            }
        } catch (error) {
            const errMsg = error.response?.data?.error || error.message || 'Erro de conexão.';
            setMessage({ type: 'error', text: errMsg });
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', display: 'flex', alignItems: 'center' }}>
                <FactoryIcon sx={{ mr: 2, color: 'primary.main' }} /> Produção
            </Typography>

            {/* Upload Section */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                    <CloudUploadIcon sx={{ mr: 1, color: 'primary.main' }} /> Importar Produção
                </Typography>
                <form onSubmit={handleUpload}>
                    <Grid container spacing={3} alignItems="flex-end">
                        <Grid item xs={12} md={9}>
                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                                startIcon={<AttachFileIcon />}
                                sx={{ height: 56, justifyContent: 'flex-start' }}
                            >
                                {file ? file.name : "Selecionar Arquivo (.xlsx, .txt, .csv)"}
                                <input
                                    id="fileInput"
                                    type="file"
                                    hidden
                                    accept=".xlsx,.txt,.csv"
                                    onChange={handleFileChange}
                                    required
                                />
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                fullWidth
                                disabled={uploading}
                                startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                                sx={{ height: 56 }}
                            >
                                {uploading ? 'Enviando...' : 'Importar'}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
                {message && (
                    <Alert severity={message.type} sx={{ mt: 3 }}>
                        {message.text}
                    </Alert>
                )}
            </Paper>

            {/* Filter & List Section */}
            <Paper sx={{ p: 3 }}>
                <Grid container spacing={3} alignItems="center" sx={{ mb: 3 }}>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>Competência</InputLabel>
                            <Select
                                value={selectedCompetencia}
                                label="Competência"
                                onChange={(e) => {
                                    setSelectedCompetencia(e.target.value);
                                    setPage(1);
                                }}
                                startAdornment={<FilterListIcon sx={{ color: 'action.active', mr: 1 }} />}
                            >
                                <MenuItem value=""><em>Selecione a Competência</em></MenuItem>
                                {competencias.map(comp => (
                                    <MenuItem key={comp} value={comp}>{comp}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <TextField
                            fullWidth
                            placeholder="Buscar..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : items.length === 0 ? (
                    <Typography variant="body1" align="center" color="text.secondary" sx={{ py: 4 }}>
                        {selectedCompetencia ? 'Nenhum item encontrado.' : 'Selecione uma competência para visualizar.'}
                    </Typography>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Registros de Produção</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color="error"
                                    onClick={handleDeleteAllProduction}
                                >
                                    Excluir Todos
                                </Button>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<FaPlus />}
                                    onClick={handleOpenAdd}
                                >
                                    Adicionar Registro
                                </Button>
                            </Box>
                        </Box>
                        {items.map((summaryItem) => {
                            const atendimento = summaryItem.atendimento || 'Sem Atendimento';
                            const totalValue = summaryItem.valor_total || 0;
                            const itemCount = summaryItem.quantidade || 0;
                            const isVisitLoading = loadingDetails[atendimento];
                            const currentVisitDetails = visitDetails[atendimento] || [];

                            return (
                                <Accordion
                                    key={atendimento}
                                    sx={{ mb: 1 }}
                                    onChange={handleAccordionToggle(atendimento)}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{
                                            bgcolor: 'action.hover',
                                            '&:hover': { bgcolor: 'action.selected' }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2, alignItems: 'center' }}>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={3}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                        {atendimento}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        {summaryItem.convenio}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        <Chip label={`Drs: ${summaryItem.tempo_permanencia || 0}`} size="small" variant="outlined" color="primary" />
                                                        <Chip label={summaryItem.medico || 'Médico N/A'} size="small" variant="outlined" />
                                                        <Chip label={summaryItem.especialidade || 'Espec. N/A'} size="small" variant="outlined" />
                                                        <Chip
                                                            label={summaryItem.procedimento_principal || 'Proc. N/A'}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ maxWidth: 200 }}
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={3} sx={{ textAlign: 'right' }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                        Total: R$ {parseFloat(totalValue).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ p: 0 }}>
                                        {isVisitLoading ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                                <CircularProgress size={24} />
                                                <Typography sx={{ ml: 2 }}>Carregando lançamentos...</Typography>
                                            </Box>
                                        ) : (
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Conta</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Qtd</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Unitário</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Setor</TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {currentVisitDetails.map((detail) => (
                                                            <TableRow key={detail.id} hover>
                                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{detail.data_lancamento}</TableCell>
                                                                <TableCell>{detail.conta}</TableCell>
                                                                <TableCell>{detail.codigo}</TableCell>
                                                                <TableCell sx={{ maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={detail.descricao}>
                                                                    {detail.descricao}
                                                                </TableCell>
                                                                <TableCell align="right">{parseFloat(detail.quantidade).toLocaleString('pt-BR')}</TableCell>
                                                                <TableCell align="right">
                                                                    {parseFloat(detail.valor_unitario).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                                    {parseFloat(detail.valor_total).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                                </TableCell>
                                                                <TableCell>{detail.centro_custo}</TableCell>
                                                                <TableCell align="center">
                                                                    <IconButton size="small" color="primary" onClick={() => handleOpenEdit(detail)}>
                                                                        <FaEdit />
                                                                    </IconButton>
                                                                    <IconButton size="small" color="error" onClick={() => setDeleteConfirmation(detail.id)}>
                                                                        <FaTrash />
                                                                    </IconButton>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                        {currentVisitDetails.length === 0 && (
                                                            <TableRow>
                                                                <TableCell colSpan={9} align="center" sx={{ py: 2 }}>
                                                                    Nenhum detalhe encontrado para este atendimento.
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                    </>
                )}

                {/* Pagination */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary">
                        Mostrando {items.length} de {total} itens
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                        >
                            Anterior
                        </Button>
                        <Typography variant="body2">
                            Página {page}
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setPage(p => p + 1)}
                            disabled={items.length < 50}
                        >
                            Próxima
                        </Button>
                    </Stack>
                </Box>
            </Paper>

            {/* Missing Cost Centers Modal */}
            <Dialog
                open={showMissingModal}
                onClose={() => setShowMissingModal(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'warning.light' }}>
                    <WarningIcon color="warning" />
                    Centros de Custo Não Cadastrados
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                        Os seguintes centros de custo encontrados na produção não estão cadastrados no sistema.
                        Selecione os que deseja cadastrar automaticamente.
                    </Alert>
                    <List dense>
                        {missingCenters.map((center) => (
                            <ListItem key={center} dense button onClick={() => toggleCenterSelection(center)}>
                                <ListItemIcon>
                                    <Checkbox
                                        edge="start"
                                        checked={selectedCenters.includes(center)}
                                        tabIndex={-1}
                                        disableRipple
                                    />
                                </ListItemIcon>
                                <ListItemText primary={center} />
                            </ListItem>
                        ))}
                    </List>
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                            label={`${selectedCenters.length} de ${missingCenters.length} selecionados`}
                            color="primary"
                            variant="outlined"
                            size="small"
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button
                        onClick={() => setShowMissingModal(false)}
                        color="inherit"
                    >
                        Ignorar
                    </Button>
                    <Button
                        onClick={() => {
                            setSelectedCenters(missingCenters);
                        }}
                        variant="outlined"
                        size="small"
                    >
                        Selecionar Todos
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleRegisterCenters}
                        disabled={registering || selectedCenters.length === 0}
                        startIcon={registering ? <CircularProgress size={16} /> : <AddCircleIcon />}
                    >
                        {registering ? 'Cadastrando...' : `Cadastrar ${selectedCenters.length} Centro(s)`}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Add/Edit Modal */}
            <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                <DialogTitle>{editingItem ? 'Editar Item' : 'Adicionar Item'}</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Centro de Custo</InputLabel>
                        <Select
                            autoFocus
                            value={formData.centro_custo}
                            label="Centro de Custo"
                            onChange={(e) => setFormData({ ...formData, centro_custo: e.target.value })}
                        >
                            <MenuItem value=""><em>Selecione...</em></MenuItem>
                            {ccOptions.map(cc => (
                                <MenuItem key={cc} value={cc}>{cc}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        margin="dense"
                        label="Quantidade"
                        type="number"
                        fullWidth
                        value={formData.quantidade}
                        onChange={(e) => setFormData({ ...formData, quantidade: e.target.value })}
                    />
                    <TextField
                        margin="dense"
                        label="Descrição"
                        fullWidth
                        multiline
                        rows={2}
                        value={formData.descricao}
                        onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    />
                    <TextField
                        margin="dense"
                        label="Valor Total"
                        type="number"
                        fullWidth
                        value={formData.valor}
                        onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained">Salvar</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={!!deleteConfirmation} onClose={() => setDeleteConfirmation(null)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <Typography>Tem certeza que deseja excluir este item?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmation(null)}>Cancelar</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">Excluir</Button>
                </DialogActions>
            </Dialog>
        </Box >
    );
};

export default ProductionPage;
